/**
 * ScaledCertificate — bulletproof mobile-safe wrapper around CertificateA4.
 *
 * Problem being solved:
 *   Real iOS Safari (all versions we've tested) inconsistently resolves
 *   `container-type: inline-size` when combined with `aspect-ratio` +
 *   `width: 100%` on the same element. The `cqi`-based font sizes inside
 *   CertificateA4 then compute against the viewport or a bogus width,
 *   blowing the layout out of the A4 frame on iPhone. Every attempt at a
 *   CSS-only fix (text-size-adjust, container-type on a nested div,
 *   -webkit-* etc.) still failed on real hardware.
 *
 * Solution:
 *   - Render CertificateA4 at a FIXED pixel width (REFERENCE_WIDTH). At
 *     that fixed width, its internal `cqi` units resolve deterministically
 *     because we're establishing a stable inline-size container.
 *   - Measure the outer wrapper via ResizeObserver and apply a CSS
 *     `transform: scale(...)` so the rendered result fits the actual
 *     available width.
 *   - Give the wrapper a computed height so it doesn't collapse and
 *     doesn't leave whitespace.
 *
 * This is completely independent of Safari's container-query bugs — the
 * cert always renders at 1000 px internally, cqi values are always
 * proportional pixels, and the final visual size is a pure transform.
 */

import { useEffect, useRef, useState } from "react";
import CertificateA4 from "./CertificateA4";

// Matches exportCertificatePDF.js's BASE_WIDTH_PX so any fixed-px value
// mixed into an otherwise cqi-sized layout (e.g. a logo's maxWidth cap)
// resolves to the same on-screen proportion as it does in the downloaded
// PDF -- previously this was 1000 while the export used 1122, which is
// why fixed-width elements rendered at a different relative size in the
// two paths and made the download not match the preview.
const REFERENCE_WIDTH = 1122;
// A4 landscape aspect ratio: 297 mm wide × 210 mm tall.
const A4_HEIGHT_RATIO = 210 / 297;

export default function ScaledCertificate(props) {
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    // Initial synchronous measure so first paint isn't at scale=1.
    const initial = el.getBoundingClientRect().width;
    if (initial > 0) setScale(initial / REFERENCE_WIDTH);

    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setScale(w / REFERENCE_WIDTH);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: "100%",
        // Height derives from scaled aspect ratio — no whitespace, no clip.
        height: `${REFERENCE_WIDTH * A4_HEIGHT_RATIO * scale}px`,
        position: "relative",
        overflow: "hidden",
      }}
      data-testid="scaled-certificate"
    >
      <div
        style={{
          width: `${REFERENCE_WIDTH}px`,
          transformOrigin: "top left",
          transform: `scale(${scale})`,
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <CertificateA4 {...props} />
      </div>
    </div>
  );
}
